import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart3, 
  TrendingUp, 
  Target,
  CheckCircle, 
  Clock, 
  ArrowRight,
  Settings,
  LogOut,
  User,
  ArrowLeft
} from 'lucide-react';
import { useLocation } from 'wouter';
import { useAuth } from '@/contexts/AuthContext';
import { useProjectContext } from '@/contexts/ProjectContext';
import redwoodMark from "@/assets/Redwood_LogoMark_White.png";
import redwoodWordmark from "@/assets/Redwood_Logo_WhiteOnly.png";

interface Project {
  id: string;
  name: string;
  description: string;
  client_org?: string;
  module_completion?: {
    maturity: number;
    roi: number;
    benchmarking: number;
    value_assessment: number;
  };
}

interface AssessmentModule {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  route: string;
  completionKey: 'maturity' | 'roi' | 'benchmarking' | 'value_assessment';
}

export default function ModuleSelectionScreen() {
  const [location, setLocation] = useLocation();
  const { user, isAuthenticated, isSuperAdmin, logout } = useAuth();
  const { projectId, project, isLoading: contextLoading, setProjectId } = useProjectContext();
  const [isLoading, setIsLoading] = useState(true);

  // Extract project ID from URL and set in context
  const urlProjectId = location.split('/')[2];
  
  useEffect(() => {
    if (urlProjectId && urlProjectId !== projectId) {
      setProjectId(urlProjectId);
    }
  }, [urlProjectId, projectId, setProjectId]);

  const modules: AssessmentModule[] = [
    {
      id: 'benchmarking',
      title: 'Benchmarking Analysis',
      description: 'Compare your R2R performance against industry benchmarks and identify optimization opportunities for cycle time and efficiency.',
      icon: TrendingUp,
      route: '/benchmarking',
      completionKey: 'benchmarking'
    },
    {
      id: 'maturity',
      title: 'Maturity Assessment',
      description: 'Evaluate your current Record-to-Report process maturity across key dimensions including automation, controls, and reporting capabilities.',
      icon: BarChart3,
      route: '/maturity',
      completionKey: 'maturity'
    },
    {
      id: 'value_assessment',
      title: 'Value Assessment',
      description: 'Comprehensive value assessment and ROI modeling for R2R transformation initiatives with dual FTE allocation modes and strategic analysis.',
      icon: Target,
      route: '/value-assessment',
      completionKey: 'value_assessment'
    }
  ];

  useEffect(() => {
    if (!projectId) {
      console.warn("No project ID available, redirecting to project selection");
      setLocation('/');
      return;
    }
    setIsLoading(false);
  }, [projectId]);

  const getModuleStatus = (module: AssessmentModule) => {
    if (!project?.module_completion) {
      return { isCompleted: false, score: 0 };
    }
    
    const score = project.module_completion[module.completionKey] || 0;
    return {
      isCompleted: score > 0,
      score: score
    };
  };

  const handleModuleClick = (module: AssessmentModule) => {
    const status = getModuleStatus(module);
    
    // Set project context in localStorage for the assessment module
    if (projectId) {
      localStorage.setItem('currentProjectId', projectId);
      localStorage.setItem('currentProjectName', project?.name || '');
    }
    
    // Build project-scoped route
    const projectRoute = `${module.route}/${projectId}`;
    
    if (status.isCompleted) {
      // Navigate to existing assessment for editing
      setLocation(projectRoute);
    } else {
      // Navigate to create new assessment
      setLocation(projectRoute);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        {/* Navigation Bar */}
        <nav className="border-b border-border bg-card">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <img src={redwoodMark} alt="Redwood mark" className="h-6 w-6" />
                <img src={redwoodWordmark} alt="Redwood" className="h-6" />
              </div>
              
              <div className="flex items-center gap-4">
                {isAuthenticated ? (
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-muted-foreground">
                      Welcome, {user?.username || user?.email}
                    </span>
                    {isSuperAdmin && (
                      <Button
                        onClick={() => setLocation('/admin')}
                        variant="outline"
                        size="sm"
                        className="text-[#4AE2C9] border-[#4AE2C9] hover:bg-[#4AE2C9] hover:text-[#0D1224]"
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Admin
                      </Button>
                    )}
                    <Button
                      onClick={() => setLocation('/dashboard')}
                      variant="outline"
                      size="sm"
                      className="text-[#4AE2C9] border-[#4AE2C9] hover:bg-[#4AE2C9] hover:text-[#0D1224]"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Dashboard
                    </Button>
                    <Button
                      onClick={logout}
                      variant="outline"
                      size="sm"
                      className="text-gray-400 border-gray-400 hover:bg-gray-400 hover:text-[#0D1224]"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </Button>
                  </div>
                ) : (
                  <Button 
                    onClick={() => setLocation('/login')}
                    variant="outline"
                    className="text-[#4AE2C9] border-[#4AE2C9] hover:bg-[#4AE2C9] hover:text-[#0D1224]"
                  >
                    Login
                  </Button>
                )}
              </div>
            </div>
          </div>
        </nav>
        
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4AE2C9] mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading project...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background">
        <nav className="border-b border-border bg-card">
          <div className="max-w-5xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <img src={redwoodMark} alt="Redwood mark" className="h-6 w-6" />
                <img src={redwoodWordmark} alt="Redwood" className="h-6" />
              </div>
            </div>
          </div>
        </nav>
        
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-4">
            <p className="text-lg text-muted-foreground">Project not found</p>
            <Button onClick={() => setLocation('/')} className="bg-[#4AE2C9] text-[#0D1224]">
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Bar */}
      <nav className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img src={redwoodMark} alt="Redwood mark" className="h-6 w-6" />
              <img src={redwoodWordmark} alt="Redwood" className="h-6" />
            </div>
            
            <div className="flex items-center gap-4">
              {isAuthenticated ? (
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    Welcome, {user?.username || user?.email}
                  </span>
                  {isSuperAdmin && (
                    <Button
                      onClick={() => setLocation('/admin')}
                      variant="outline"
                      size="sm"
                      className="text-[#4AE2C9] border-[#4AE2C9] hover:bg-[#4AE2C9] hover:text-[#0D1224]"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Admin
                    </Button>
                  )}
                  <Button
                    onClick={() => setLocation('/dashboard')}
                    variant="outline"
                    size="sm"
                    className="text-[#4AE2C9] border-[#4AE2C9] hover:bg-[#4AE2C9] hover:text-[#0D1224]"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Dashboard
                  </Button>
                  <Button
                    onClick={logout}
                    variant="outline"
                    size="sm"
                    className="text-gray-400 border-gray-400 hover:bg-gray-400 hover:text-[#0D1224]"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </Button>
                </div>
              ) : (
                <Button 
                  onClick={() => setLocation('/login')}
                  variant="outline"
                  className="text-[#4AE2C9] border-[#4AE2C9] hover:bg-[#4AE2C9] hover:text-[#0D1224]"
                >
                  Login
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        {/* Back Button */}
        <Button
          onClick={() => setLocation('/')}
          variant="outline"
          className="border-border text-foreground hover:bg-muted"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        {/* Project Header */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              {project.client_org && (
                <Badge className="bg-[#4AE2C9] text-[#0D1224] hover:bg-[#4AE2C9]/90">
                  {project.client_org}
                </Badge>
              )}
              <h1 className="text-3xl font-bold text-foreground">{project.name}</h1>
              <p className="text-lg text-muted-foreground">{project.description}</p>
            </div>
          </div>
        </div>

        {/* Module Selection */}
        <div className="space-y-6">
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-foreground">Select Assessment Module</h2>
            <p className="text-muted-foreground">
              Choose a module to begin or continue your R2R assessment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {modules.map((module) => {
              const status = getModuleStatus(module);
              const IconComponent = module.icon;

              return (
                <Card
                  key={module.id}
                  className="relative cursor-pointer border-[#4AE2C9]/30 hover:border-[#4AE2C9] hover:shadow-lg hover:shadow-[#4AE2C9]/20 transition-all duration-300 group"
                  onClick={() => handleModuleClick(module)}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-3 flex-1">
                        <div className={`p-3 rounded-lg w-fit ${status.isCompleted ? 'bg-[#4AE2C9] text-[#0D1224]' : 'bg-muted'}`}>
                          <IconComponent className="h-6 w-6" />
                        </div>
                        
                        <CardTitle className="text-lg font-bold text-foreground group-hover:text-[#4AE2C9] transition-colors">
                          {module.title}
                        </CardTitle>
                      </div>
                      
                      <div className="bg-muted rounded-full p-2 group-hover:bg-[#4AE2C9] group-hover:text-[#0D1224] transition-all">
                        <ArrowRight className="h-4 w-4" />
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {module.description}
                    </p>
                    
                    {/* Status */}
                    <div className="flex items-center justify-between bg-muted/50 rounded-lg p-3">
                      <div className="flex items-center space-x-2">
                        {status.isCompleted ? (
                          <>
                            <CheckCircle className="h-4 w-4 text-[#4AE2C9]" />
                            <span className="text-sm font-medium text-foreground">Completed</span>
                          </>
                        ) : (
                          <>
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-medium text-muted-foreground">Not Started</span>
                          </>
                        )}
                      </div>
                      
                      {status.isCompleted && (
                        <Badge variant="secondary" className="bg-[#4AE2C9]/20 text-[#4AE2C9]">
                          {status.score}%
                        </Badge>
                      )}
                    </div>

                    {/* Action Text */}
                    <div className="text-center pt-2">
                      <span className="text-sm text-[#4AE2C9] font-medium">
                        {status.isCompleted ? 'Click to edit assessment' : 'Click to start assessment'}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}